import Image from 'next/image';
import classNames from 'classnames';

import TileFeaturedBadge from './tile-featured-badge';
import { Tile as TileProps, TilesItem } from './tiles.types';

interface TilesProps {
  items: TilesItem[];
  className?: string;
}

const resolveLink = (link: string | (() => string)) =>
  typeof link === 'function' ? link() : link;

export const Tiles = ({ items, className = '' }: TilesProps) => {
  return (
    <nav
      aria-label="سرویس‌های تپسی دکتر"
      className={classNames(
        'w-full grid grid-cols-12 gap-3',
        !className?.includes('py-') && 'py-3',
        className,
      )}
    >
      {items.map((item) => {
        return <Tile key={resolveLink(item.link)} {...item} />;
      })}
    </nav>
  );
};

export const Tile = ({
  size = 'quarter',
  title,
  titleAlign = 'center',
  titlePosition = 'outside',
  imageAlign = 'center',
  link,
  imageUrl,
  featured,
  badgeText,
  className = '',
}: TileProps) => {
  const href = resolveLink(link);

  const titleAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[titleAlign];

  const sizeClass = {
    quarter: 'col-span-3',
    third: 'col-span-4',
    half: 'col-span-6',
    wide: 'col-span-12',
  }[size];
  const imageSizeClass = {
    quarter: { w: 56, h: 56 },
    third: { w: 64, h: 64 },
    half: { w: 64, h: 64 },
    wide: { w: 72, h: 72 },
  }[size];

  const imageAlignClass = {
    center: 'justify-center',
    left: 'justify-start',
    right: 'justify-end',
  }[imageAlign];

  return (
    <a
      href={href}
      className={classNames(
        sizeClass,
        'flex flex-col justify-center items-center gap-1.5 text-xs no-underline',
        className,
      )}
    >
      <div className={classNames("relative w-full", size === 'quarter' ? 'xs:pt-[110%] sm:pt-[84%]': 'pt-[88%]')}>
        <div
          className={classNames(
            size === 'quarter' ? 'py-1' : 'py-2',
            'absolute inset-0 flex justify-center items-center flex-col px-2 bg-grey-50 rounded-lg',
          )}
        >
          {badgeText && (
            <span className="flex items-center justify-center px-2 py-[2px] text-white text-Label/2xSmall text-center absolute -top-[5px] -left-[5px] bg-surface-accent rounded-full">
              {badgeText}
            </span>
          )}
          {featured && <TileFeaturedBadge featured={featured} />}
          <div
            className={classNames(
              'flex items-center w-full h-full',
              imageAlignClass,
            )}
          >
            <Image
              src={imageUrl}
              alt={title}
              width={imageSizeClass.w}
              height={imageSizeClass.h}
              unoptimized
              style={{
                width: `${imageSizeClass.w}px`,
                height: `${imageSizeClass.h}px`,
                objectFit: 'contain',
              }}
            />
          </div>
          {titlePosition === 'inside' && (
            <div className="flex items-center justify-center w-full h-auto">
              <div
                className={classNames(
                  'text-surface-inverse-primary text-nowrap text-Label/2xSmall xs:text-Label/xSmall',
                  titleAlignClass,
                )}
              >
                {title}
              </div>
            </div>
          )}
        </div>
      </div>
      {titlePosition === 'outside' && (
        <div className="w-full flex items-center justify-center">
          <div className="w-full h-full rounded-lg flex justify-center items-center">
            <div
              className={classNames(
                'text-surface-inverse-primary font-medium w-full text-nowrap',
                titleAlignClass,
              )}
            >
              {title}
            </div>
          </div>
        </div>
      )}
    </a>
  );
};
